import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  Self,
  Optional,
  OnInit,
  Input,
  AfterViewInit,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostBinding,
} from '@angular/core';
import { NgControl, AbstractControl, FormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { GasFormBaseComponent } from '../../gas-form-base-component';
import { GasForm } from '../../gas-form.namespace';
import { GasAutocompleteOptions } from '../gas-autocomplete.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * @example
 * <gas-autocomplete
 *  [formControl]="control"
 *  [options]="options"
 *  label="Автокомплит"
 * ></gas-autocomplete>
 */
@UntilDestroy()
@Component({
  selector: 'gas-autocomplete',
  templateUrl: './gas-autocomplete.component.html',
  styleUrls: ['./gas-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasAutocompleteComponent extends GasFormBaseComponent implements OnInit, OnChanges, AfterViewInit {
  @HostBinding('class.input-disabled')
  public get inputDisabled(): boolean {
    return !!this.nativeDisabled;
  }

  @ViewChild('inputRef')
  public inputRef: ElementRef;

  @Output('inputChange')
  public inputChange$ = new EventEmitter<string>();

  @Input()
  public label: string;

  @Input()
  public placeholder: string;

  @Input()
  public error: string;

  @Input()
  public loading = false;

  @Input()
  public defaultFilter = false;

  //todo: интерфейс должен быть дженериком
  @Input()
  public options: GasAutocompleteOptions[] = [];

  @Input()
  public dropdownSameWidth = false;

  public nativeValueSubject$ = new BehaviorSubject<string>('');
  public loading$ = new BehaviorSubject<boolean>(false);

  private typeSubject$ = new BehaviorSubject<GasForm.InputState | string>(this.state || GasForm.InputState.Default);

  public get control(): AbstractControl {
    return this.ngControl?.control;
  }

  public inputControl = new FormControl('');

  //todo: value контрола не обязательно должно быть объектом и иметь поле title
  public get autoCompleteValue(): string {
    return this.control?.value?.title || '';
  }

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    this.control?.statusChanges.pipe(
      startWith(this.ngControl.control.status),
      untilDestroyed(this),
    ).subscribe((status: GasForm.States) => {
      this.nativeDisabled = status === GasForm.States.Disabled;
      this.changeDetectorRef.markForCheck();
    });

    this.nativeValueSubject$.subscribe(val => this.inputControl.patchValue(val));

    //todo: value контрола не обязательно должно быть объектом и иметь поле title
    this.nativeValueSubject$.next(this.control?.value?.title || '');

    if (this.control) {
      this.control.valueChanges.subscribe(value => {
        this.nativeValueSubject$.next(value?.title || '');
      });
    }
  }

  public ngAfterViewInit(): void {
    this.typeSubject$.pipe(
      untilDestroyed(this),
    ).subscribe((type: GasForm.InputState | string) => this.setAutocompleteType(type));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading !== undefined && changes.loading.previousValue !== changes.loading.currentValue) {
      this.loading$.next(changes.loading.currentValue);
    }

    if (changes.state && changes.state.previousValue !== changes.state.currentValue) {
      this.typeSubject$.next(changes.state.currentValue);
    }

    if (changes.options && changes.options.previousValue !== changes.options.currentValue) {
      this.nativeValueSubject$.next(this.nativeValueSubject$.value);
    }
  }

  public changeInputValue(event: Event): void {
    this.nativeValueSubject$.next((event.target as HTMLInputElement).value);
    this.inputChange$.emit(this.nativeValueSubject$.value);
  }

  public clearValue(): void {
    this.control.setValue(null, {emitEvent: false});
  }

  public writeValue(value: any): void {
    super.writeValue(value);

    if (!value) {
      this.nativeValueSubject$.next('');
      this.inputRef.nativeElement.value = '';
      this.inputChange$.emit('');
    }
  }

  /**
   * @param type - состояние автокомплита
   * меняет класс в зависимости от текущего состояния
   */
  private setAutocompleteType(type: GasForm.InputState | string): void {
    const autocomplete: HTMLElement = this.elementRef.nativeElement;
    const types: GasForm.InputState[] = [
      GasForm.InputState.Default, GasForm.InputState.Primary, GasForm.InputState.Success, GasForm.InputState.Error,
    ];

    types
      .filter((filteredType: GasForm.InputState) => filteredType !== type)
      .forEach((eachType: GasForm.InputState) => {
        this.renderer.removeClass(autocomplete, `input-${eachType}`);
      });

    this.renderer.addClass(autocomplete, `input-${type}`);
  }
}
